/**
 * M02 L1 intent prompt template editor.
 *
 * This is the runtime prompt body used by L1Fallback. Merchant gray config below
 * only points to version_id values defined here.
 */
import { useEffect, useState } from "react";

import { useL1PromptTemplates, usePutL1PromptTemplates } from "@/lib/api/intent";

interface ApiErrorBody {
	detail?: string;
}

function extractErrorDetail(err: unknown): string {
	if (typeof err === "object" && err !== null) {
		const maybeAxios = err as { response?: { data?: ApiErrorBody }; message?: string };
		if (maybeAxios.response?.data?.detail) return maybeAxios.response.data.detail;
		if (maybeAxios.message) return maybeAxios.message;
	}
	return String(err);
}

export function L1PromptTemplateEditor() {
	const templates = useL1PromptTemplates();
	const putMutation = usePutL1PromptTemplates();
	const [yamlText, setYamlText] = useState("");
	const [savedAt, setSavedAt] = useState<string | null>(null);
	const [submitErr, setSubmitErr] = useState<string | null>(null);

	useEffect(() => {
		if (templates.data) {
			setYamlText(templates.data.yaml_text);
			setSavedAt(null);
			setSubmitErr(null);
		}
	}, [templates.data]);

	const handleSave = () => {
		if (!templates.data) return;
		setSavedAt(null);
		setSubmitErr(null);
		putMutation.mutate(
			{
				yaml_text: yamlText,
				expected_active_version: templates.data.active_version,
			},
			{
				onSuccess: () => setSavedAt(new Date().toLocaleTimeString()),
				onError: (err) => setSubmitErr(extractErrorDetail(err)),
			},
		);
	};

	if (templates.isLoading) {
		return (
			<section data-testid="l1-prompt-template-editor" className="text-sm text-text-secondary">
				加载 L1 提示词模板...
			</section>
		);
	}

	if (templates.isError) {
		return (
			<section data-testid="l1-prompt-template-editor" className="text-sm text-brand-danger">
				L1 提示词模板加载失败：{extractErrorDetail(templates.error)}
			</section>
		);
	}

	const data = templates.data;
	if (!data) return null;

	return (
		<section
			data-testid="l1-prompt-template-editor"
			className="space-y-3 border border-surface-border p-4"
		>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<div className="text-sm font-semibold text-text-primary">L1 意图分类提示词模板</div>
					<div className="mt-1 text-xs text-text-muted">
						当前生效版本 {data.active_version} · 共 {data.templates_total} 个版本
					</div>
				</div>
				<div className="text-right text-xs text-text-secondary">
					<div>{data.current_template.title}</div>
					<div>{new Date(data.current_template.updated_at).toLocaleString()}</div>
				</div>
			</div>

			<textarea
				data-testid="l1-prompt-yaml-textarea"
				value={yamlText}
				onChange={(event) => setYamlText(event.target.value)}
				className="h-[360px] w-full resize-y border border-surface-border bg-surface-card px-3 py-2 font-mono text-xs leading-5 text-text-primary outline-none focus:border-brand-accent"
				spellCheck={false}
			/>

			<div className="flex flex-wrap items-center gap-3">
				<button
					type="button"
					data-testid="l1-prompt-template-save"
					className="rounded-md border border-brand-accent bg-brand-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
					onClick={handleSave}
					disabled={putMutation.isPending || yamlText.trim() === ""}
				>
					{putMutation.isPending ? "保存中..." : "保存模板"}
				</button>
				<button
					type="button"
					data-testid="l1-prompt-template-reload"
					className="rounded-md border border-surface-border px-3 py-1.5 text-sm hover:bg-surface-subtle"
					onClick={() => templates.refetch()}
				>
					重载
				</button>
				{savedAt ? (
					<span
						data-testid="l1-prompt-template-saved-at"
						className="border border-brand-success/30 bg-brand-success/10 px-2 py-0.5 text-xs text-brand-success"
					>
						已保存 {savedAt}
					</span>
				) : null}
			</div>

			{submitErr ? (
				<p
					data-testid="l1-prompt-template-error"
					className="border border-brand-danger bg-brand-danger/10 p-2 text-xs text-brand-danger"
				>
					{submitErr}
				</p>
			) : null}
		</section>
	);
}
